import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AuthContext } from '../utils/AuthContent';
import { canReceiveNotifications } from '../utils/roleAdapter';
import { fetchNotificationsList, markNotificationsRead } from '../service/notification.service';
import notificationSocket, { buildNotificationSocketUrl } from '../service/notificationSocket';
import {
  NOTIFICATIONS_PAGE_SIZE,
  NotificationRecord,
} from '../components/Notification/NotificationTypes';

// Module-level: one Audio instance, reused for every push instead of
// allocating one per notification.
const notificationSound = new Audio('/mixkit-message-pop-alert-2354.mp3');

const playNotificationSound = () => {
  notificationSound.currentTime = 0;
  // Autoplay can be blocked before the user has interacted with the page --
  // that rejection is expected and not worth surfacing.
  notificationSound.play().catch(() => undefined);
};

// Pages can shift between requests, so drop repeats by id when appending.
const mergeNotifications = (
  current: NotificationRecord[],
  incoming: NotificationRecord[],
) => {
  const seenIds: Record<string, boolean> = {};
  const merged: NotificationRecord[] = [];

  current.concat(incoming).forEach((item) => {
    if (seenIds[item.id]) return;
    seenIds[item.id] = true;
    merged.push(item);
  });
  return merged;
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  // Bumped only when a live notification actually lands, so the UI can react to it.
  const [lastReceivedAt, setLastReceivedAt] = useState(0);

  const loginData = useSelector((state: any) => state.loginResource);
  const { keycloak } = useContext(AuthContext);
  // "Workspace" in the UI is preferredProject in the store. Profile is a
  // separate signal: changeProfile() switches it without touching the project.
  const projectId = loginData?.preferredProject;
  const preferredProfile = loginData?.preferredProfile;
  const isLoggedIn = Boolean(loginData?.loggedIn);
  // Only the user view (Business Analyst) gets notifications; Workspace Owner
  // does not. Derived from preferredProfile + profileData, so switching role
  // flips this and the effects below tear down.
  const isEnabled = canReceiveNotifications(loginData);

  // loginResource is spread from the /umbackend/login JWT and no other code
  // reads an email from it, so the claim name isn't guaranteed. Keycloak's
  // parsed token is the dependable fallback. Note `username` is deliberately
  // not in this chain -- it is a handle, not an address.
  const userEmail =
    loginData?.email ||
    loginData?.user_email ||
    (keycloak?.tokenParsed as any)?.email ||
    (keycloak as any)?.profile?.email ||
    '';

  const nextOffsetRef = useRef(0);
  const isFetchingRef = useRef(false);
  const notificationsRef = useRef<NotificationRecord[]>([]);
  // Incremented whenever the workspace/profile context changes, so a response
  // from the previous context can be recognised and discarded.
  const generationRef = useRef(0);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const resetState = useCallback(() => {
    nextOffsetRef.current = 0;
    setNotifications([]);
    setUnreadCount(0);
    setHasMore(true);
    setLastReceivedAt(0);
  }, []);

  const load = useCallback(
    async (offset: number, append: boolean) => {
      if (!projectId) return;
      // Block only concurrent appends. A first-page load must be able to
      // supersede an in-flight request, or switching workspace mid-fetch
      // leaves the previous workspace's rows on screen for good.
      if (append && isFetchingRef.current) return;

      const generation = generationRef.current;
      isFetchingRef.current = true;

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result = await fetchNotificationsList(projectId, offset, true);
        // Workspace/profile changed while this was in flight: drop the result.
        if (generation !== generationRef.current) return;

        // null means the call failed. Show nothing rather than inventing rows,
        // and stop paging so the scroll handler doesn't retry in a loop.
        if (!result) {
          if (!append) {
            setNotifications([]);
            setUnreadCount(0);
          }
          setHasMore(false);
          return;
        }

        const page = result.notifications;

        setNotifications((current) => (append ? mergeNotifications(current, page) : page));
        setUnreadCount(result.unread_count ?? 0);
        nextOffsetRef.current = offset + page.length;
        // A short page means the server has nothing left to give.
        setHasMore(page.length === NOTIFICATIONS_PAGE_SIZE);
      } finally {
        if (generation === generationRef.current) {
          isFetchingRef.current = false;
          setIsLoadingMore(false);
          setIsLoading(false);
        }
      }
    },
    [projectId],
  );

  // Gate on loginResource being populated, not just on Keycloak: the app renders
  // before the HQI access_token exists, so an earlier call would go out unauthorized.
  // Re-runs on workspace switch and on profile switch.
  useEffect(() => {
    generationRef.current += 1;
    isFetchingRef.current = false;
    resetState();

    if (!isLoggedIn || !projectId || !isEnabled) return;
    load(0, false);
  }, [isLoggedIn, projectId, preferredProfile, isEnabled, load, resetState]);

  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingRef.current) return;
    load(nextOffsetRef.current, true);
  }, [hasMore, load]);

  /** Push a live notification to the top of the list. */
  const addNotification = useCallback((record: NotificationRecord) => {
    // The socket can repeat a record that the list already holds.
    if (notificationsRef.current.some((item) => item.id === record.id)) return;

    setNotifications((current) => [record, ...current]);
    if (!record.is_read) setUnreadCount((count) => count + 1);
    // The server list grew by one, so shift the paging cursor to match.
    nextOffsetRef.current += 1;
    setLastReceivedAt(Date.now());
    playNotificationSound();
  }, []);

  const handleSocketMessage = useCallback(
    (message: any) => {
      if (!message || typeof message.type !== 'string') return;

      if (message.type === 'connected') {
        setIsConnected(true);
        return;
      }
      // Keepalive every 30s while idle -- not app data, nothing to show.
      if (message.type === 'heartbeat') return;
      if (message.type !== 'notification') return;

      const record = message.data;
      if (!record || typeof record.id !== 'number') return;

      addNotification(record as NotificationRecord);
    },
    [addNotification],
  );

  // One socket per workspace. Tears down on logout, workspace switch and
  // profile switch; the cleanup runs before the next connect.
  //
  // preferredProfile is part of the gate, not just the key: it lands a render
  // or two after preferredProject during login hydration, and connecting on
  // the intermediate state meant the profile arriving immediately tore down a
  // socket that was still mid-handshake -- one wasted connection per login
  // plus a "closed before the connection is established" error.
  useEffect(() => {
    // No email means the server closes with 4401 straight away, so don't dial.
    if (!isLoggedIn || !projectId || !preferredProfile || !userEmail || !isEnabled) {
      notificationSocket.disconnect();
      setIsConnected(false);
      return;
    }

    notificationSocket.connect(buildNotificationSocketUrl(projectId, userEmail));
    const unsubscribe = notificationSocket.subscribe(handleSocketMessage);
    return () => {
      unsubscribe();
      notificationSocket.disconnect();
      setIsConnected(false);
    };
  }, [isLoggedIn, projectId, preferredProfile, userEmail, isEnabled, handleSocketMessage]);

  /**
   * Marks one notification read and drops it from the list. The panel is an
   * unread inbox, so a read notification has no place in it.
   */
  const markAsRead = useCallback(
    async (id: number) => {
      const index = notifications.findIndex((item) => item.id === id);
      if (index === -1 || !projectId) return;
      const target = notifications[index];

      // Optimistic: the panel closes on click, so waiting for the round trip
      // would leave the row visibly behind.
      setNotifications((current) => current.filter((item) => item.id !== id));
      setUnreadCount((count) => Math.max(count - 1, 0));

      const updated = await markNotificationsRead(projectId, id);
      if (updated !== null) return;

      // Failed: restore it at its original position rather than silently
      // dropping something the server still considers unread.
      setNotifications((current) => {
        if (current.some((item) => item.id === id)) return current;
        const restored = current.slice();
        restored.splice(Math.min(index, restored.length), 0, target);
        return restored;
      });
      setUnreadCount((count) => count + 1);
    },
    [notifications, projectId],
  );

  /** Marks every unread notification read, emptying the list. */
  const markAllAsRead = useCallback(async () => {
    if (!projectId || notifications.length === 0) return;

    const snapshot = notifications;
    const previousCount = unreadCount;
    setNotifications([]);
    setUnreadCount(0);

    const updated = await markNotificationsRead(projectId);
    if (updated !== null) return;

    setNotifications(snapshot);
    setUnreadCount(previousCount);
  }, [notifications, projectId, unreadCount]);

  return {
    notifications,
    unreadCount,
    isLoading,
    isLoadingMore,
    hasMore,
    isConnected,
    isEnabled,
    lastReceivedAt,
    loadMore,
    markAsRead,
    markAllAsRead,
    addNotification,
    refresh: () => load(0, false),
  };
};

export default useNotifications;
